import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  ShoppingCart, 
  UserCog, 
  Calculator, 
  Briefcase, 
  Package, 
  GitBranch,
  Loader2,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  User,
  Calendar,
  Building,
  FileIcon,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Action name translations for Turkish
const translateAction = (action) => {
  const translations = {
    'created': 'Oluşturuldu',
    'branch_approved': 'Şube Onayladı',
    'branch_rejected': 'Şube Reddetti',
    'branch_forwarded': 'Şube İK\'ya İletti',
    'forwarded': 'İletildi',
    'hr_approved': 'İK Onayladı',
    'hr_rejected': 'İK Reddetti',
    'hr_forwarded': 'İK Yönetime İletti',
    'forward_management': 'Yönetime İletildi',
    'management_approved': 'Yönetim Onayladı',
    'management_rejected': 'Yönetim Reddetti',
    'approved': 'Onaylandı',
    'rejected': 'Reddedildi',
    'pending': 'Beklemede',
    'pending_hr': 'İK Bekliyor',
    'pending_management': 'Yönetim Bekliyor',
    'submitted': 'Gönderildi',
    'updated': 'Güncellendi'
  };
  return translations[action] || action;
};

// Get note from action_history for a specific action type
const getActionNote = (actionHistory, actionTypes) => {
  if (!actionHistory || !Array.isArray(actionHistory)) return null;
  
  // Find the most recent matching action
  for (let i = actionHistory.length - 1; i >= 0; i--) {
    const action = actionHistory[i];
    if (actionTypes.includes(action.action)) {
      return action.note || action.description || null;
    }
  }
  return null;
};

const BoardTalepler = () => {
  const { branchId } = useParams();
  const { branches } = useOutletContext();
  const [activeTab, setActiveTab] = useState('satin-alma');
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState({
    'satin-alma': [],
    'insan-kaynaklari': [],
    'muhasebe': [],
    'idari-isler': [],
    'depo': [],
    'is-akisi': []
  });
  
  // Modal states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [actionNote, setActionNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Get branch name from context
  const branch = branches?.find(b => b.id === branchId);
  const branchName = branch?.name || 'Şube';

  const departmentTabs = [
    { key: 'satin-alma', icon: ShoppingCart, label: 'Satın Alma' },
    { key: 'insan-kaynaklari', icon: UserCog, label: 'İnsan Kaynakları' },
    { key: 'muhasebe', icon: Calculator, label: 'Muhasebe' },
    { key: 'idari-isler', icon: Briefcase, label: 'İdari İşler' },
    { key: 'depo', icon: Package, label: 'Depo' },
    { key: 'is-akisi', icon: GitBranch, label: 'İş Akışı' }
  ];

  useEffect(() => {
    fetchAllRequests();
  }, [branchId]);

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // İnsan Kaynakları için özel endpoint kullan
      const newRequests = {};
      
      // İnsan Kaynakları - pending_management status'undaki talepler
      try {
        const hrResponse = await fetch(`${API_URL}/api/board/branches/${branchId}/hr-requests/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (hrResponse.ok) {
          const hrData = await hrResponse.json();
          newRequests['insan-kaynaklari'] = hrData;
        } else {
          newRequests['insan-kaynaklari'] = [];
        }
      } catch (e) {
        newRequests['insan-kaynaklari'] = [];
      }
      
      // Diğer departmanlar için eski endpoint'leri dene
      const otherDepts = departmentTabs.filter(d => d.key !== 'insan-kaynaklari');
      const fetchPromises = otherDepts.map(async (dept) => {
        try {
          const response = await fetch(`${API_URL}/api/board/branch/${branchId}/${dept.key}/requests`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            return { key: dept.key, data };
          }
        } catch (e) {
          // If endpoint doesn't exist, return empty array
        }
        return { key: dept.key, data: [] };
      });

      const results = await Promise.all(fetchPromises);
      results.forEach(r => {
        newRequests[r.key] = r.data;
      });
      setRequests(newRequests);
    } catch (error) {
      console.error('Talepler yüklenemedi:', error);
      toast.error('Talepler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_management':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" /> Yönetim Onayı Bekliyor</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Onaylandı</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> Reddedildi</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Düşük', color: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', color: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', color: 'bg-red-100 text-red-600' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  // Open detail modal
  const openDetailModal = async (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
    
    // Mark request as viewed by board
    try {
      const token = localStorage.getItem('fortis_token');
      await fetch(`${API_URL}/api/board/hr-requests/${request.id}/view`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Görüntüleme işaretlenemedi:', error);
    }
  };

  // Open action modal
  const openActionModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setActionNote('');
    setShowActionModal(true);
  };

  // Handle approve/reject action
  const handleAction = async () => {
    if (!selectedRequest) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const requestId = selectedRequest.id;
      
      // Determine endpoint based on request source - use query parameters
      let endpoint;
      const noteParam = actionNote ? `&note=${encodeURIComponent(actionNote)}` : '';
      
      if (selectedRequest.source === 'work_panel') {
        endpoint = `${API_URL}/api/board/work-requests/${requestId}/action?action=${actionType}${noteParam}`;
      } else {
        endpoint = `${API_URL}/api/board/hr-requests/${requestId}/action?action=${actionType}${noteParam}`;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success(actionType === 'approve' ? 'Talep onaylandı' : 'Talep reddedildi');
        setShowActionModal(false);
        setShowDetailModal(false);
        fetchAllRequests(); // Refresh data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Action error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const renderRequestList = (deptKey) => {
    const deptRequests = requests[deptKey] || [];
    
    if (deptRequests.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium">Bu departmanda talep bulunmuyor</p>
          <p className="text-sm mt-1">Henüz hiç talep oluşturulmamış</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Talep No</TableHead>
              <TableHead className="w-[100px]">Tarih</TableHead>
              <TableHead className="w-[120px]">Kategori</TableHead>
              <TableHead>İş Konusu</TableHead>
              <TableHead className="w-[150px]">Belge Adı</TableHead>
              <TableHead className="w-[100px]">Öncelik</TableHead>
              <TableHead className="w-[160px]">Durum</TableHead>
              <TableHead className="w-[120px] text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deptRequests.map((request, index) => (
              <TableRow 
                key={request.id || index} 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => openDetailModal(request)}
                data-testid={`request-row-${request.id}`}
              >
                <TableCell>
                  <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                    {request.form_number || `#${index + 1}`}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    {request.created_at ? new Date(request.created_at).toLocaleDateString('tr-TR') : '-'}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {request.request_type || request.type || request.category || '-'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium line-clamp-1">{request.title || request.description || '-'}</p>
                    {request.description && request.title && (
                      <p className="text-sm text-gray-500 truncate max-w-[200px]">{request.description}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {request.documents && request.documents.length > 0 ? (
                    <div className="flex items-center gap-1 text-sm">
                      <FileText className="h-4 w-4 text-amber-500" />
                      <span className="truncate max-w-[120px]">{request.documents[0].name || request.documents[0].filename}</span>
                      {request.documents.length > 1 && (
                        <span className="text-xs text-gray-400">+{request.documents.length - 1}</span>
                      )}
                    </div>
                  ) : request.form_data?.belgeler?.length > 0 ? (
                    <div className="flex items-center gap-1 text-sm">
                      <FileText className="h-4 w-4 text-amber-500" />
                      <span className="truncate max-w-[120px]">{request.form_data.belgeler[0].name}</span>
                      {request.form_data.belgeler.length > 1 && (
                        <span className="text-xs text-gray-400">+{request.form_data.belgeler.length - 1}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {getPriorityBadge(request.priority)}
                </TableCell>
                <TableCell>
                  {getStatusBadge(request.status)}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openDetailModal(request)} title="Detay Görüntüle">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {request.status === 'pending_management' && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => openActionModal(request, 'approve')}
                          title="Onayla"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => openActionModal(request, 'reject')}
                          title="Reddet"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-500))]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-talepler">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">
          {branchName} - Talepler
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">
          Departman bazlı talepler ve istekler
        </p>
      </div>

      {/* Department Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-6 w-full h-auto p-1 bg-gray-100 rounded-lg">
          {departmentTabs.map((dept) => {
            const Icon = dept.icon;
            const count = (requests[dept.key] || []).length;
            return (
              <TabsTrigger
                key={dept.key}
                value={dept.key}
                className="flex flex-col items-center gap-1 py-3 px-2 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
                data-testid={`tab-${dept.key}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{dept.label}</span>
                {count > 0 && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {count}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {departmentTabs.map((dept) => (
          <TabsContent key={dept.key} value={dept.key} className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <dept.icon className="h-5 w-5 text-[hsl(var(--brand-600))]" />
                  {dept.label} Talepleri
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderRequestList(dept.key)}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Talep Detayı - {selectedRequest?.form_number || selectedRequest?.title || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              Talep bilgileri ve işlem geçmişi
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="font-medium">Durum:</span>
                {getStatusBadge(selectedRequest.status)}
              </div>
              
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Form No</Label>
                  <p className="font-medium">{selectedRequest.form_number || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Talep Türü</Label>
                  <p className="font-medium">{selectedRequest.request_type || selectedRequest.type || selectedRequest.category || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="font-medium">
                    {selectedRequest.created_at ? new Date(selectedRequest.created_at).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Talep Eden</Label>
                  <p className="font-medium flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {selectedRequest.requester_name || selectedRequest.owner_name || selectedRequest.created_by_name || '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Şube</Label>
                  <p className="font-medium flex items-center gap-1">
                    <Building className="h-4 w-4" />
                    {branchName}
                  </p>
                </div>
                {(selectedRequest.title || selectedRequest.name) && (
                  <div>
                    <Label className="text-gray-500">Form İsmi</Label>
                    <p className="font-medium">{selectedRequest.title || selectedRequest.name}</p>
                  </div>
                )}
              </div>

              {/* Branch Approval/Forward Info */}
              {(selectedRequest.branch_approval?.approved || selectedRequest.branch_approved || selectedRequest.branch_approval?.forwarded) && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <Label className="text-green-700 font-medium">Şube Onayı</Label>
                  <div className="mt-2 text-sm text-green-700">
                    <p className="flex items-center gap-1 font-medium">
                      <CheckCircle className="h-4 w-4" />
                      {selectedRequest.branch_approval?.forwarded 
                        ? "Şube onayladı ve İK'ya iletti" 
                        : "Şube tarafından onaylandı"}
                    </p>
                    <div className="mt-2 space-y-1 text-green-600">
                      {(selectedRequest.branch_approval?.approved_by || selectedRequest.branch_approved_by || selectedRequest.branch_approval?.forwarded_by_name) && (
                        <p className="text-xs">
                          <span className="font-medium">Onaylayan:</span> {selectedRequest.branch_approval?.approved_by || selectedRequest.branch_approved_by || selectedRequest.branch_approval?.forwarded_by_name}
                        </p>
                      )}
                      {(selectedRequest.branch_approval?.approved_at || selectedRequest.branch_approved_at || selectedRequest.branch_approval?.timestamp) && (
                        <p className="text-xs">
                          <span className="font-medium">Tarih:</span> {new Date(selectedRequest.branch_approval?.approved_at || selectedRequest.branch_approved_at || selectedRequest.branch_approval?.timestamp).toLocaleString('tr-TR')}
                        </p>
                      )}
                    </div>
                    {selectedRequest.branch_approval?.note && (
                      <div className="mt-2 p-2 bg-green-100 rounded text-xs">
                        <span className="font-medium">Açıklama:</span>
                        <p className="mt-1">{selectedRequest.branch_approval.note}</p>
                      </div>
                    )}
                    {selectedRequest.branch_approval?.documents?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium">Ekli Belgeler:</span>
                        <div className="mt-1 space-y-1">
                          {selectedRequest.branch_approval.documents.map((doc, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-1.5 bg-green-100 rounded text-xs">
                              <FileIcon className="h-3 w-3" />
                              <span className="flex-1">{doc.name || doc.filename || `Belge ${idx + 1}`}</span>
                              {(doc.url || doc.data) && (
                                <a href={doc.url || doc.data} target="_blank" rel="noopener noreferrer" className="text-green-800 hover:underline">
                                  <Download className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* HR Approval/Forward Info */}
              {(selectedRequest.hr_approval?.approved || selectedRequest.hr_approved || selectedRequest.hr_approval?.forwarded) && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <Label className="text-blue-700 font-medium">İK Onayı</Label>
                  <div className="mt-2 text-sm text-blue-700">
                    <p className="flex items-center gap-1 font-medium">
                      <CheckCircle className="h-4 w-4" />
                      {selectedRequest.hr_approval?.forwarded 
                        ? "İK onayladı ve yönetime iletti" 
                        : "İK tarafından onaylandı"}
                    </p>
                    <div className="mt-2 space-y-1 text-blue-600">
                      {(selectedRequest.hr_approval?.approved_by || selectedRequest.hr_approved_by || selectedRequest.hr_approval?.forwarded_by_name) && (
                        <p className="text-xs">
                          <span className="font-medium">Onaylayan:</span> {selectedRequest.hr_approval?.approved_by || selectedRequest.hr_approved_by || selectedRequest.hr_approval?.forwarded_by_name}
                        </p>
                      )}
                      {(selectedRequest.hr_approval?.approved_at || selectedRequest.hr_approved_at || selectedRequest.hr_approval?.timestamp) && (
                        <p className="text-xs">
                          <span className="font-medium">Tarih:</span> {new Date(selectedRequest.hr_approval?.approved_at || selectedRequest.hr_approved_at || selectedRequest.hr_approval?.timestamp).toLocaleString('tr-TR')}
                        </p>
                      )}
                    </div>
                    {(selectedRequest.hr_approval?.note || getActionNote(selectedRequest.action_history, ['forwarded', 'hr_forwarded', 'hr_approved'])) && (
                      <div className="mt-2 p-2 bg-blue-100 rounded text-xs">
                        <span className="font-medium">Açıklama:</span>
                        <p className="mt-1">{selectedRequest.hr_approval?.note || getActionNote(selectedRequest.action_history, ['forwarded', 'hr_forwarded', 'hr_approved'])}</p>
                      </div>
                    )}
                    {selectedRequest.hr_approval?.documents?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium">Ekli Belgeler:</span>
                        <div className="mt-1 space-y-1">
                          {selectedRequest.hr_approval.documents.map((doc, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-1.5 bg-blue-100 rounded text-xs">
                              <FileIcon className="h-3 w-3" />
                              <span className="flex-1">{doc.name || doc.filename || `Belge ${idx + 1}`}</span>
                              {(doc.url || doc.data) && (
                                <a href={doc.url || doc.data} target="_blank" rel="noopener noreferrer" className="text-blue-800 hover:underline">
                                  <Download className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description/Title */}
              {(selectedRequest.description || selectedRequest.title) && (
                <div>
                  <Label className="text-gray-500">Talep Açıklaması</Label>
                  <p className="mt-1 p-3 bg-gray-50 rounded-md">
                    {selectedRequest.description || selectedRequest.title}
                  </p>
                </div>
              )}

              {/* Approval Notes - Şube ve İK'nın yazdığı açıklamalar */}
              {(selectedRequest.branch_approval?.note || selectedRequest.hr_approval?.note || getActionNote(selectedRequest.action_history, ['forwarded', 'hr_forwarded', 'branch_forwarded'])) && (
                <div>
                  <Label className="text-gray-500">Onay Açıklamaları</Label>
                  <div className="mt-2 space-y-2">
                    {/* Şube Açıklaması */}
                    {selectedRequest.branch_approval?.note && (
                      <div className="p-3 bg-green-50 border-l-4 border-green-500 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-green-700">Şube Yönetimi</span>
                          {selectedRequest.branch_approval?.forwarded_by_name && (
                            <span className="text-xs text-green-600">({selectedRequest.branch_approval.forwarded_by_name})</span>
                          )}
                        </div>
                        <p className="text-sm text-green-800">{selectedRequest.branch_approval.note}</p>
                        {selectedRequest.branch_approval?.timestamp && (
                          <p className="text-xs text-green-500 mt-1">
                            {new Date(selectedRequest.branch_approval.timestamp).toLocaleString('tr-TR')}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* İK Açıklaması */}
                    {(selectedRequest.hr_approval?.note || getActionNote(selectedRequest.action_history, ['forwarded', 'hr_forwarded'])) && (
                      <div className="p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-blue-700">İnsan Kaynakları</span>
                          {selectedRequest.hr_approval?.forwarded_by_name && (
                            <span className="text-xs text-blue-600">({selectedRequest.hr_approval.forwarded_by_name})</span>
                          )}
                        </div>
                        <p className="text-sm text-blue-800">
                          {selectedRequest.hr_approval?.note || getActionNote(selectedRequest.action_history, ['forwarded', 'hr_forwarded'])}
                        </p>
                        {selectedRequest.hr_approval?.timestamp && (
                          <p className="text-xs text-blue-500 mt-1">
                            {new Date(selectedRequest.hr_approval.timestamp).toLocaleString('tr-TR')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Form Data */}
              {selectedRequest.form_data && (
                <div>
                  <Label className="text-gray-500">Form Bilgileri</Label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md space-y-2">
                    {Object.entries(selectedRequest.form_data.form_data || selectedRequest.form_data).map(([key, value]) => {
                      if (typeof value === 'object' || key === 'belgeler') return null;
                      return (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="font-medium">{String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Documents */}
              {(selectedRequest.documents?.length > 0 || selectedRequest.form_data?.belgeler?.length > 0) && (
                <div>
                  <Label className="text-gray-500">Ekli Belgeler</Label>
                  <div className="mt-2 space-y-3">
                    {[...(selectedRequest.documents || []), ...(selectedRequest.form_data?.belgeler || [])].map((doc, idx) => {
                      const isImage = doc.type?.startsWith('image/') || 
                                     doc.name?.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);
                      const fileData = doc.data || doc.url;
                      
                      return (
                        <div key={idx} className="p-3 bg-gray-50 rounded-md border">
                          {/* Image Preview */}
                          {isImage && fileData && (
                            <div className="mb-2">
                              <img 
                                src={fileData} 
                                alt={doc.name || `Belge ${idx + 1}`}
                                className="max-h-48 rounded border object-contain mx-auto"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                          
                          {/* File Info & Download */}
                          <div className="flex items-center gap-2">
                            <FileIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            <span className="flex-1 text-sm font-medium truncate">
                              {doc.name || doc.filename || `Belge ${idx + 1}`}
                            </span>
                            {doc.size && (
                              <span className="text-xs text-gray-400">
                                ({Math.round(doc.size / 1024)} KB)
                              </span>
                            )}
                            {fileData && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  // Create download link
                                  const link = document.createElement('a');
                                  link.href = fileData;
                                  link.download = doc.name || doc.filename || `belge_${idx + 1}`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                İndir
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action History */}
              {selectedRequest.action_history?.length > 0 && (
                <div>
                  <Label className="text-gray-500">İşlem Geçmişi</Label>
                  <div className="mt-2 space-y-2">
                    {selectedRequest.action_history.map((action, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-sm">
                        <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div>
                          <p className="font-medium">{translateAction(action.action)} - {action.user_name || action.by || 'Sistem'}</p>
                          {action.note && <p className="text-gray-500">{action.note}</p>}
                          <p className="text-xs text-gray-400">
                            {(action.timestamp || action.at) ? new Date(action.timestamp || action.at).toLocaleString('tr-TR') : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            {selectedRequest?.status === 'pending_management' && (
              <>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => {
                    setShowDetailModal(false);
                    openActionModal(selectedRequest, 'reject');
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reddet
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => {
                    setShowDetailModal(false);
                    openActionModal(selectedRequest, 'approve');
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Onayla
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Modal (Approve/Reject) */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === 'approve' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              {actionType === 'approve' ? 'Talebi Onayla' : 'Talebi Reddet'}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.form_number || selectedRequest?.title || 'N/A'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="action-note">
                {actionType === 'approve' ? 'Onay Notu (Opsiyonel)' : 'Red Sebebi'}
              </Label>
              <Textarea
                id="action-note"
                placeholder={actionType === 'approve' ? 'Onay için not ekleyin...' : 'Red sebebini belirtin...'}
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)} disabled={actionLoading}>
              İptal
            </Button>
            <Button 
              className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={handleAction}
              disabled={actionLoading || (actionType === 'reject' && !actionNote)}
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionType === 'approve' ? 'Onayla' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardTalepler;
